import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';

import { cn } from '../cn';

import { Input, type InputProps } from './input';

export type PasswordInputProps = Omit<InputProps, 'type'> & {
  /** aria-label for the toggle button while the password is hidden (i.e. the button's action is "show"). */
  showLabel?: string;
  /** aria-label for the toggle button while the password is visible (i.e. the button's action is "hide"). */
  hideLabel?: string;
};

/**
 * `Input` fixed to a password field, with a trailing eye toggle so users can
 * reveal what they typed. Same DESIGN.md "Inputs / Fields" chrome as `Input`;
 * the toggle button sits inside the field's padding, never shifting layout.
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, showLabel = 'Show password', hideLabel = 'Hide password', ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);

    return (
      <div className="relative">
        <Input ref={ref} type={visible ? 'text' : 'password'} className={cn('pe-9', className)} {...props} />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          className="absolute inset-y-0 end-0 flex w-9 items-center justify-center text-muted hover:text-ink"
        >
          {visible ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
